import {Box, Link} from '@mui/material';
import {type ReactElement, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useToken} from '@/components/TokenContext';

// eslint-disable-next-line @typescript-eslint/naming-convention
const redirect_uri = `${document.location.origin}/`;
// eslint-disable-next-line @typescript-eslint/naming-convention
const client_id = import.meta.env.VITE_APP_CLIENT_ID;
const api = import.meta.env.VITE_APP_API_ENDPOINT;
const scope =
    'read:jira-work write:issue:jira write:attachment:jira write:comment:jira write:comment.property:jira read:issue:jira';

type Props = {
    children: ReactElement;
    keepKeys: string[];
};

const Home = ({children, keepKeys}: Props): ReactElement => {
    const {token, setToken} = useToken();
    const [status, setStatus] = useState('Loading...');
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code');
    const navigate = useNavigate();
    const {pathname} = useLocation();

    const authorze = useMemo(() => {
        const redirect = new URL(`${document.location.origin}/`);

        for (const key of keepKeys) {
            redirect.searchParams.set(key, searchParams.get(key) ?? '');
        }

        redirect.searchParams.set('returnPath', pathname);

        const authorize = new URL('https://auth.atlassian.com/authorize');
        authorize.searchParams.set('audience', 'api.atlassian.com');
        authorize.searchParams.set('client_id', client_id);
        authorize.searchParams.set('scope', scope);
        authorize.searchParams.set('redirect_uri', redirect.toString());
        authorize.searchParams.set('response_type', 'code');
        authorize.searchParams.set('prompt', 'consent');

        return authorize.toString();
    }, [searchParams, keepKeys, pathname]);

    useEffect(() => {
        if (!(code || token)) {
            // if (!webhookId) {
            //     setStatus('Missing webhook id');
            //     return;
            // }

            window.location.href = authorze;
        } else if (!token) {
            const payload = JSON.stringify({
                grant_type: 'authorization_code',
                client_id,
                redirect_uri,
                code,
            });

            fetch(new URL('/public/atlassian-exchange/', api), {
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'POST',
                body: payload,
            })
                .then(async response => {
                    const oauthResponse = (await response.json()) as Record<string, unknown>;
                    const token =
                        'access_token' in oauthResponse ? oauthResponse.access_token : undefined;
                    const errorDescription =
                        'error_description' in oauthResponse
                            ? oauthResponse.error_description
                            : undefined;

                    if (typeof token === 'string') {
                        setStatus('Received token...');
                        setToken(token);

                        const expresIn =
                            'expires_in' in oauthResponse &&
                            typeof oauthResponse.expires_in === 'number'
                                ? oauthResponse.expires_in
                                : undefined;

                        setTimeout(() => {
                            setStatus('');
                        }, 5000);

                        if (expresIn) {
                            //10 seconds before the expires time reload
                            setTimeout(
                                () => {
                                    window.location.href = authorze;
                                },
                                (expresIn - 10) * 1000,
                            );
                        }

                        const returnPath = searchParams.get('returnPath') ?? '/';

                        if (
                            returnPath !== pathname &&
                            !returnPath.includes(':') &&
                            !returnPath.includes('//')
                        ) {
                            console.log('redirecting');
                            navigate(`${returnPath}?${searchParams.toString()}`);
                        }
                    } else if (typeof errorDescription === 'string') {
                        setStatus(`Error exchanging token: ${errorDescription}`);
                    } else {
                        setStatus('Failed to exchange token.');
                    }
                })
                .catch((e: unknown) => {
                    console.error('token', e);
                    setStatus('Error Exchanging token');
                });
        }
    }, []);

    return (
        <Box sx={{backgroundColor: '#F3F3F3', minHeight: '100vh'}}>
            <Box sx={{mx: 'auto', width: '500px', p: 3}}>
                <Box sx={{width: '100%', backgroundColor: '#FFFFFF', p: 3}}>
                    <Link href={authorze}>Re-Login</Link>
                    <br />
                    {status}
                    {token && children}
                </Box>
            </Box>
        </Box>
    );
};

export default Home;
